import { useRef, useState } from 'react'
import { useLang } from '../i18n/LanguageContext'
import { COMMUNICATION_API_BASE } from '../config/apiBase'
import './ContactPage.css'

// The culture the confirmation mail is written in: the language the visitor has the site in.
const CULTURES = { nb: 'nb-NO', en: 'en-US', sv: 'sv-SE', da: 'da-DK', fi: 'fi-FI', is: 'is-IS', fo: 'fo-FO' }

const EMPTY = { name: '', email: '', organization: '', subject: '', message: '', website: '' }

function ContactPage() {
  const { t, lang } = useLang()
  const [form, setForm] = useState(EMPTY)
  const [status, setStatus] = useState(null) // null, 'sending', 'success', 'error', 'tooMany'

  // When the form was shown. A program sends it at once; a person needs a few seconds. The server turns the quick ones away without a word.
  const shownAt = useRef(Date.now())

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('sending')

    try {
      const response = await fetch(`${COMMUNICATION_API_BASE}/v1/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          organization: form.organization || null,
          subject: form.subject || 'general',
          message: form.message,
          culture: CULTURES[lang] || 'nb-NO',
          website: form.website,
          fillSeconds: (Date.now() - shownAt.current) / 1000,
        }),
      })

      if (response.ok) {
        setStatus('success')
        setForm(EMPTY)
        shownAt.current = Date.now()
      } else {
        setStatus(response.status === 429 ? 'tooMany' : 'error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1 className="hero-title" style={{textAlign: 'center'}}>
            {t('contact.heroTitle')}
          </h1>
          <p className="hero-subtitle" style={{textAlign: 'center', maxWidth: '500px', margin: '0 auto'}}>
            {t('contact.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2>{t('contact.formTitle')}</h2>
              <p>{t('contact.formIntro')}</p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">📧</div>
                  <div className="method-info">
                    <h4>{t('contact.email')}</h4>
                    <a href="mailto:post@communitynord.com">post@communitynord.com</a>
                  </div>
                </div>
              </div>

              <p className="text-sm" style={{ marginTop: 'var(--space-6)' }}>{t('contact.supportNote')}</p>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              {status === 'success' && <div className="alert alert-success" role="status">{t('contact.success')}</div>}
              {status === 'error' && <div className="alert alert-error" role="alert">{t('contact.error')}</div>}
              {status === 'tooMany' && <div className="alert alert-error" role="alert">{t('contact.tooMany')}</div>}

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">{t('contact.name')} *</label>
                  <input type="text" id="name" name="name" className="form-input" required maxLength={200} value={form.name} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">{t('contact.email')} *</label>
                  <input type="email" id="email" name="email" className="form-input" required maxLength={320} value={form.email} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="organization" className="form-label">{t('contact.organization')}</label>
                  <input type="text" id="organization" name="organization" className="form-input" maxLength={200} value={form.organization} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="subject" className="form-label">{t('contact.subject')} *</label>
                  <select id="subject" name="subject" className="form-select" required value={form.subject} onChange={handleChange}>
                    <option value="">{t('contact.chooseSubject')}</option>
                    <option value="general">{t('contact.subjectGeneral')}</option>
                    <option value="pricing">{t('contact.subjectPricing')}</option>
                    <option value="support">{t('contact.subjectSupport')}</option>
                    <option value="demo">{t('contact.subjectDemo')}</option>
                    <option value="partnership">{t('contact.subjectPartnership')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">{t('contact.message')} *</label>
                  <textarea id="message" name="message" rows="5" className="form-textarea" required maxLength={5000} value={form.message} onChange={handleChange}></textarea>
                </div>

                {/* The hidden field: a person never sees it and never fills it in, a program that fills in every field does. */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label htmlFor="website">Website</label>
                  <input type="text" id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={handleChange} />
                </div>

                <button type="submit" className="btn btn-primary btn-lg" disabled={status === 'sending'}>
                  {status === 'sending' ? t('contact.sending') : t('contact.send')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactPage
